import {
    Dispatch,
    ReactNode,
    SetStateAction,
    createContext,
    createElement,
    useContext,
    useState,
} from 'react';
import { z } from 'zod';
import { useParamState } from '../../../hooks/useParamState';
import { Experience } from '../../../types/experience';
import { useNavigate } from 'react-router';
import { ExperienceType } from '../../../enums/experience';

const steps = [
    'details',
    'location',
    'amenities',
    'availability',
    'tracking',
    'guides',
    'pricing',
    'discounts',
    'policies',
    'access',
    'integrations',
    'photos',
    'videos',
    'floorplans',
] as const;

type Step = (typeof steps)[number];
type Workflow = 'create' | 'edit';

function getExperienceFormSteps(type: ExperienceType): Step[] {
    if (type === ExperienceType.Vehicle)
        return steps.filter((step) => step !== 'floorplans');
    if (type === ExperienceType.Activity)
        return steps.filter(
            (step) => step !== 'floorplans' && step !== 'tracking',
        );
    return steps.filter((step) => step !== 'tracking');
}

interface FormStateContextValue {
    /** Experience currently being edited in the multi-step form. */
    experience: Experience;
    /** Active form step synced from the `step` URL query param. */
    step: Step;
    /** Ordered list of valid steps for the current experience type. */
    steps: Step[];
    /**
     * Moves to the next step.
     * Navigates to review when called on the final step.
     */
    handleNext: () => void;
    /**
     * Moves to the previous step.
     * No-op when already on the first step.
     */
    handlePrevious: () => void;
    /**
     * Jumps to a specific step when it is valid for this experience type.
     * Invalid steps are ignored.
     */
    handleSelect: (step: Step) => void;
    /** Zero-based index of the active step inside `steps`. */
    stepIndex: number;
    /** Determines if this form is creating or editing an experience. */
    workflow: Workflow;
    /**
     * When true, save actions (Save, Save and Exit, Next) are disabled.
     * Use while a long-running action is in progress, such as a media upload.
     */
    isSaveDisabled: boolean;
    /** Enables or disables save actions for the current form. */
    setIsSaveDisabled: Dispatch<SetStateAction<boolean>>;
}

const FormStateContext = createContext<FormStateContextValue | null>(null);

function FormStateProvider({
    children,
    experience,
}: {
    children: ReactNode;
    experience: Experience;
}) {
    const navigate = useNavigate();
    const workflow = experience.status === 'draft' ? 'create' : 'edit';
    const [isSaveDisabled, setIsSaveDisabled] = useState(false);

    const [step, setStep] = useParamState('step', {
        type: z.enum(steps),
        defaultValue:
            workflow === 'create' ? experience.currentStep : 'details',
        replace: false,
    });

    const stepsForExperience = getExperienceFormSteps(experience.type);
    const activeStep = stepsForExperience.includes(step)
        ? step
        : stepsForExperience[0];
    const stepIndex = stepsForExperience.indexOf(activeStep);

    const handleNext = () => {
        if (stepIndex === stepsForExperience.length - 1) {
            navigate(`/experiences/new/${experience.id}/review`);
            return;
        }
        setStep(stepsForExperience[stepIndex + 1]);
    };

    const handlePrevious = () => {
        if (stepIndex === 0) return;
        setStep(stepsForExperience[stepIndex - 1]);
    };

    const handleSelect = (step: Step) => {
        if (!stepsForExperience.includes(step)) return;
        setStep(step);
    };

    const value: FormStateContextValue = {
        experience,
        steps: stepsForExperience,
        step: activeStep,
        stepIndex,
        workflow,
        handleNext,
        handlePrevious,
        handleSelect,
        isSaveDisabled,
        setIsSaveDisabled,
    };

    return createElement(FormStateContext.Provider, { value }, children);
}

/**
 * Provides access to the current experience form navigation state.
 *
 * Use this hook inside components rendered within `FormStateProvider` to:
 * - read the current step (`step`, `stepIndex`, `steps`)
 * - access the current `experience`
 * - navigate between steps (`handleNext`, `handlePrevious`, `handleSelect`)
 * - disable save actions during long-running work (`isSaveDisabled`, `setIsSaveDisabled`)
 *
 * @example
 * const { step, handleNext, handlePrevious, setIsSaveDisabled } = useFormState();
 */
function useFormState(): FormStateContextValue {
    const context = useContext(FormStateContext);
    if (context === null) {
        throw new Error('useFormState must be used within a FormStateProvider');
    }

    return context;
}

export { FormStateProvider, steps, useFormState };
export type { FormStateContextValue, Step };
